import { HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import React, { Suspense } from 'react';

import Layout from './components/Layout';
import Login from './components/Login';
import { ToastProvider } from './components/Toast';
import { QueryProvider } from './providers/QueryProvider';

// 路由级懒加载：每个页面拆分为独立 chunk，按需加载
const Calendar = React.lazy(() => import('./components/Calendar'));
const CoachManagement = React.lazy(() => import('./components/CoachManagement'));
const CourseList = React.lazy(() => import('./components/CourseList'));
const CustomerKanban = React.lazy(() => import('./components/CustomerKanban'));
const DataExport = React.lazy(() => import('./components/data-export'));
const PackageManagement = React.lazy(() => import('./components/PackageManagement'));
const PackagePricingConfig = React.lazy(() => import('./components/PackagePricingConfig'));
const ReportCenter = React.lazy(() => import('./components/ReportCenter'));
const Settings = React.lazy(() => import('./components/settings'));
const StudentManagement = React.lazy(() => import('./components/StudentManagement'));
const StudentProgress = React.lazy(() => import('./components/student-progress'));
const LessonPlanList = React.lazy(() => import('./components/LessonPlanList'));
const LessonPlanView = React.lazy(() => import('./components/LessonPlanView'));
const LessonPlanEditor = React.lazy(() => import('./components/LessonPlanEditor'));
const LessonPlanGraph = React.lazy(() => import('./components/LessonPlanGraph'));
const TacticalCapsules = React.lazy(() => import('./components/tactical-capsules'));
const VenueManagement = React.lazy(() => import('./components/VenueManagement'));
const VideoAnalysisPage = React.lazy(() => import('./components/video-analysis'));
const SharedReportPage = React.lazy(() => import('./components/video-analysis/SharedReportPage'));

// 全局加载占位符
const PageLoader: React.FC = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3 text-gray-500">
      <div className="w-10 h-10 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin"></div>
      <span>加载中...</span>
    </div>
  </div>
);

// 路由守卫组件
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

// 受保护的 Layout 包装：合并 ProtectedRoute + Layout + Suspense，避免每个路由重复
const ProtectedLayout: React.FC = () => (
  <ProtectedRoute>
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </Layout>
  </ProtectedRoute>
);

// ErrorBoundary 错误边界组件
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('应用错误:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">应用出错了</h1>
            <p className="text-gray-600 mb-4">请刷新页面重试</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600"
            >
              刷新页面
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryProvider>
        <ToastProvider>
          <HashRouter>
          <Routes>
            {/* 登录页不需要认证 */}
            <Route path="/login" element={<Login />} />

            {/* 公开分享页：免登录 */}
            <Route
              path="/share/video-analysis/:token"
              element={
                <Suspense fallback={<PageLoader />}>
                  <SharedReportPage />
                </Suspense>
              }
            />

            {/* 受保护的路由统一通过 ProtectedLayout 包裹 */}
            <Route element={<ProtectedLayout />}>
              <Route path="/" element={<Navigate to="/calendar" replace />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/courses" element={<CourseList />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/coach-management" element={<CoachManagement />} />
              <Route path="/student-management" element={<StudentManagement />} />
              <Route path="/packages" element={<PackageManagement />} />
              <Route path="/package-pricing" element={<PackagePricingConfig />} />
              <Route path="/progress" element={<StudentProgress />} />
              <Route path="/kanban" element={<CustomerKanban />} />
              <Route path="/reports" element={<ReportCenter />} />
              <Route path="/data-export" element={<DataExport />} />
              <Route path="/lesson-plans" element={<LessonPlanList />} />
              <Route path="/lesson-plans/new" element={<LessonPlanEditor />} />
              <Route path="/lesson-plans/graph" element={<LessonPlanGraph />} />
              <Route path="/lesson-plans/:id" element={<LessonPlanView />} />
              <Route path="/lesson-plans/:id/edit" element={<LessonPlanEditor />} />
              <Route path="/tactical-capsules" element={<TacticalCapsules />} />
              <Route path="/tactical-capsules/:id" element={<TacticalCapsules />} />
              <Route path="/venue-management" element={<VenueManagement />} />
              <Route path="/video-analysis" element={<VideoAnalysisPage />} />
            </Route>

            {/* 兜底：其他路径回到日历 */}
            <Route path="*" element={<Navigate to="/calendar" replace />} />
          </Routes>
        </HashRouter>
        </ToastProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
};

export default App;
